import { useCallback, useEffect, useState } from 'react';

const getCookie = (name: string): string | null => {
	if (typeof document === 'undefined') return null;
	const match = document.cookie.match(
		new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
	);
	return match ? decodeURIComponent(match[1]) : null;
};

const setSessionCookie = (name: string, value: string): void => {
	if (typeof document === 'undefined') return;
	// Session cookie: no expires/max-age; include path and SameSite for safety
	document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
};

const parseConsentCookie = (value: string): Record<string, unknown> | null => {
	try {
		return JSON.parse(value) as Record<string, unknown>;
	} catch {
		return null;
	}
};

export const useCookieHandler = () => {
	const [alreadyCaptured, setAlreadyCaptured] = useState(false);

	const isMarketingConsentAccepted = useCallback((): boolean => {
		const possibleCookies = ['CookieScriptConsent'];

		return possibleCookies.some((c) => {
			const val = getCookie(c);
			if (!val) {
				return false;
			}

			const obj = parseConsentCookie(val);
			if (!obj) {
				return false;
			}

			return obj.action === 'accept';
		});
	}, []);

	const captureCookieData = useCallback(() => {
		if (typeof document === 'undefined' || typeof window === 'undefined') return;

		// Respect marketing consent
		if (!isMarketingConsentAccepted()) return;

		// Do not overwrite existing session cookies once set during the session
		const existingUtmSource = getCookie('utm_source');
		const existingUtmMedium = getCookie('utm_medium');
		const existingUtmCampaign = getCookie('utm_campaign');
		const existingReferrer = getCookie('referrer');

		const hasExistingSessionCookies = !!(
			existingUtmSource ||
			existingUtmMedium ||
			existingUtmCampaign ||
			existingReferrer
		);

		if (hasExistingSessionCookies || alreadyCaptured) {
			return; // Nothing to do; respect "shouldn't be overwritten in the session"
		}

		const urlParams = new URLSearchParams(window.location.search ?? '');

		// Only set values if relevant information is present; otherwise store empty string
		const utm_source = (urlParams.get('utm_source') || '').trim();
		const utm_medium = (urlParams.get('utm_medium') || '').trim();
		const utm_campaign = (urlParams.get('utm_campaign') || '').trim();

		// Referrer: specific referrer URL as-is; empty if direct visitor
		// document.referrer is empty on direct hits or certain navigation types
		const referrer = (document.referrer || '').trim();

		setSessionCookie('utm_source', utm_source);
		setSessionCookie('utm_medium', utm_medium);
		setSessionCookie('utm_campaign', utm_campaign);
		setSessionCookie('referrer', referrer);

		setAlreadyCaptured(true);
	}, [alreadyCaptured, isMarketingConsentAccepted]);

	// Polling is used as a fallback because consent tools often update cookies without mutating the DOM.
	useEffect(() => {
		if (typeof document === 'undefined' || typeof window === 'undefined' || alreadyCaptured) return;

		const checkAndCaptureCookies = () => {
			if (!isMarketingConsentAccepted()) {
				return;
			}

			captureCookieData();
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				checkAndCaptureCookies();
			}
		};

		const intervalId = window.setInterval(checkAndCaptureCookies, 1000);

		// To make sure cookie changes are picked up correctly if the user changes their mind after the first load and accepts the cookies
		window.addEventListener('focus', checkAndCaptureCookies);
		window.addEventListener('pageshow', checkAndCaptureCookies);
		document.addEventListener('visibilitychange', handleVisibilityChange);

		checkAndCaptureCookies();

		return () => {
			window.clearInterval(intervalId);
			window.removeEventListener('focus', checkAndCaptureCookies);
			window.removeEventListener('pageshow', checkAndCaptureCookies);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [alreadyCaptured, captureCookieData, isMarketingConsentAccepted]);
};
